/**
 * Static configuration for the Credit Checker tab.
 *
 * The tab is a single scrollable surface composed of ordered sections. A
 * sticky left icon-rail (see credit-checker-nav.js) reflects this same order,
 * expands its labels on hover, and scroll-spies the active section.
 */

// Section identifiers. Used as DOM ids (for scroll anchoring) and as nav keys.
export const SECTION_IDS = {
    CASE_SUMMARY: "case-summary",
    CASE_SUMMARY_REMARKS: "case-summary-remarks",
    UPDATED_TVR: "updated-tvr",
    CAM_ASSESSMENT: "cam-assessment",
    EXISTING_PORTFOLIO: "existing-portfolio",
    COMPANY_DETAIL: "company-detail",
    APPLICANT_DETAILS: "applicant-details",
    PRODUCT_DETAILS: "product-details",
    BUREAU_LOAN: "bureau-loan",
    TRIANGULATION: "triangulation",
    ACTIONABLE: "actionable",
    PRICING: "pricing",
    CREDIT_DECISION: "credit-decision",
    CREDIT_BRIEF: "credit-brief",
    FINANCIAL_ANALYSIS: "financial-analysis",
    RECOMMENDATION: "recommendation"
};

// Columns for the Existing Portfolio "Limit Info" table (mirrors the Case
// Details summary). `money` -> ₹ grouped, `path` -> dotted nested lookup.
export const PORTFOLIO_LIMIT_COLUMNS = [
    { key: "business_product_type", label: "Product Type" },
    // Capped width; long seller names truncate with an ellipsis ("Name…") and the
    // full value shows on hover (tooltip), instead of stretching the column.
    { key: "seller_name", label: "Seller Name", width: 240, truncate: true },
    { key: "facility_status", label: "Status", status: true },
    { key: "throughput", label: "Throughput", money: true },
    { key: "sanction_limit", label: "Sanctioned Limit", money: true },
    { key: "adhoc_limit", label: "Adhoc Limit", money: true },
    { key: "adhoc_limit_end_date", label: "Adhoc Limit Expiry", date: true },
    { key: "total_receivable", label: "Current OS", money: true },
    { key: "overdue_amount", label: "OD", money: true },
    { key: "blended_utilization", label: "Blended Utilization", percent: true },
    { key: "churn", label: "# Churn", number: true },
    { key: "od_invoices", label: "OD invoices", number: true },
    { key: "max_dpd_12m", label: "Max DPD in LTM", number: true },
    { key: "max_dpd", label: "Max DPD", number: true },
    { key: "prcn_od_invoices", label: "% of OD invoices", number: true },
    {
        key: "prcn_od_30_ltm_invoices",
        label: "% of OD>30 dpd in LTM",
        number: true
    },
    // Same fields the Case Summary limit table shows (sanction-view rows).
    { key: "tenure", label: "Tenure", number: true },
    { key: "emi_paid", label: "EMI Paid", number: true },
    { key: "emi_bounce", label: "EMI Bounce", number: true }
];

// Columns for the Existing Portfolio "All Cases" table — the detailed per-case
// account-summary parameters (same data as the Case Details "previous cases"
// cards), laid out one row per case.
export const PORTFOLIO_CASE_COLUMNS = [
    // Frozen block (Case ID → Anchor) — see PortfolioCasesTable. Keep new columns
    // out of it: everything up to and including Anchor is pinned left, so adding
    // to this run eats the scrollable width.
    { key: "caseId", label: "Case ID" },
    { key: "case_status", label: "Case Status", status: true },
    { key: "fi_partner", label: "FI Partner" },
    { key: "sellers", label: "Seller" },
    { key: "anchors", label: "Anchor" },
    // Scrolls from here on.
    { key: "parent_case_id", label: "Parent Case ID" },
    { key: "proposed_or_sanction_limit", label: "Proposed / Sanctioned Limit", money: true },
    { key: "case_type", label: "Case Type" },
    { key: "product_type", label: "Product Type" },
    { key: "scheme_type", label: "Scheme Type" },
    { key: "current_stage", label: "Current Stage" },
    { key: "fi_movement", label: "FI Movement" },
    { key: "renewal", label: "Case Renewal" },
    { key: "closure_type", label: "Closure Type" },
    { key: "is_bundled", label: "Is Bundled", bool: true },
    { key: "is_rotation_case", label: "Is Rotation Case", bool: true },
    { key: "is_balance_transfer_case", label: "Is Balance Transfer Case", bool: true },
    { key: "sanction_date", label: "Sanction Date", date: true },
    { key: "last_disbursal_date", label: "Last Disbursal Date", date: true },
    { key: "expiry_date", label: "Limit Expiry", expiry: true, date: true },
    { key: "sanction_limit", label: "Sanctioned Limit", money: true },
    { key: "adhoc_limit", label: "Adhoc Limit", money: true },
    { key: "loaded_limit", label: "Loaded Limit", money: true },
    { key: "avg_utilization", label: "Avg Utilization", money: true },
    { key: "repayment_amount", label: "Total Repayment", money: true },
    { key: "throughput", label: "Throughput 12M", money: true },
    { key: "max_dpd", label: "Max DPD", number: true },
    { key: "dpd_6m", label: "DPD 6M", number: true },
    { key: "tenure", label: "Tenure", number: true },
    { key: "emi_paid", label: "EMI Paid", number: true },
    { key: "emi_bounce", label: "EMI Bounce", number: true },
    { key: "first_disbursal_ageing", label: "1st Disbursal Ageing", number: true },
    { key: "max_ageing", label: "Max Ageing", number: true },
    { key: "max_ageing_12m", label: "Max Ageing 12M", number: true },
    { key: "perc_od", label: "% Inv in OD", number: true },
    { key: "invoices_12m", label: "Inv Funded 12M", number: true },
    { key: "od_30", label: "OD < 30", money: true },
    { key: "od_30_60", label: "OD 30–60", money: true },
    { key: "od_60_90", label: "OD 60–90", money: true },
    { key: "od_90", label: "OD > 90", money: true }
];

// Limit validity window: a sanctioned working-capital limit is valid for this
// many months from its sanction date (the backend exposes no explicit expiry on
// the account summary, so the All Cases grid derives one). A case is flagged
// "expiring soon" when its derived expiry falls within this many days.
export const LIMIT_VALIDITY_MONTHS = 12;
export const LIMIT_EXPIRING_SOON_DAYS = 60;

// Case statuses that mean the limit is no longer live — these are never flagged
// as "expiring" (their expiry is moot). Matched case-insensitively as substrings.
export const INACTIVE_CASE_STATUS_HINTS = [
    "closed",
    "rejected",
    "reject",
    "hold",
    "cancel",
    "decline",
    "withdraw"
];

// Ordered list that drives both the rendered sections and the left nav rail.
// `icon` values are Ant Design v3 icon types.
export const CREDIT_CHECKER_SECTIONS = [
    {
        id: SECTION_IDS.COMPANY_DETAIL,
        label: "Company Detail",
        icon: "bank"
    },
    {
        // Existing Relationship moved up here (right after Company Detail)
        // per 2026-07-22 ask — reviewer reads the borrower's live Progfin
        // exposure immediately after identity, before per-applicant KYC.
        id: SECTION_IDS.EXISTING_PORTFOLIO,
        label: "Existing Relationship",
        icon: "pie-chart"
    },
    {
        id: SECTION_IDS.APPLICANT_DETAILS,
        label: "Applicant Details",
        icon: "idcard"
    },
    {
        id: SECTION_IDS.PRODUCT_DETAILS,
        label: "Product Details",
        icon: "profile",
        defaultCollapsed: true
    },
    {
        id: SECTION_IDS.CASE_SUMMARY,
        label: "Limit Assessment",
        // Phone icon was a hangover from when this section held Case Summary
        // / call notes. Bar-chart reads much closer to "amounts + limits",
        // which is what this section is about.
        icon: "bar-chart"
    },
    {
        id: SECTION_IDS.BUREAU_LOAN,
        label: "Current Loan Obligation",
        icon: "credit-card"
    },
    {
        id: SECTION_IDS.TRIANGULATION,
        label: "GST / Ledger / Banking",
        icon: "fund"
    },
    {
        // Full Financial Analysis tab embedded inline — only shown for cases
        // with proposed_limit ≤ ₹50 L (the tab is guard-rendered in
        // credit-checker index.js). Ships expanded by default.
        id: SECTION_IDS.FINANCIAL_ANALYSIS,
        label: "Financial Analysis",
        icon: "line-chart"
    },
    {
        id: SECTION_IDS.CAM_ASSESSMENT,
        label: "Credit Assessment",
        icon: "safety-certificate"
    },
    {
        id: SECTION_IDS.UPDATED_TVR,
        label: "TVR",
        icon: "snippets"
    },
    {
        // Case Summary (rich-text remarks) — same editor + save/undo flow the
        // Credit Appraisal tab uses, placed right below TVR.
        id: SECTION_IDS.CASE_SUMMARY_REMARKS,
        label: "Case Summary",
        icon: "solution"
    },
    {
        id: SECTION_IDS.ACTIONABLE,
        label: "Actionable",
        icon: "tags"
    },
    {
        id: SECTION_IDS.PRICING,
        label: "Pricing",
        icon: "tag"
    },
    {
        id: SECTION_IDS.CREDIT_DECISION,
        label: "Credit Decision",
        icon: "audit"
    },
    {
        id: SECTION_IDS.CREDIT_BRIEF,
        label: "Credit Brief",
        icon: "read"
    }
    // Recommendation section disabled for now.
    // {
    //     id: SECTION_IDS.RECOMMENDATION,
    //     label: "Recommendation",
    //     icon: "file-done"
    // }
];

// Recommendation document matcher (RECEIVED_DOCUMENTS "Recommendation Letter"
// = 11). Matched the tolerant way (candidate ids OR document_name substrings)
// from the merged company + case document lists.
export const RECOMMENDATION_DOCUMENT_MATCH = {
    ids: [11],
    names: ["recommendation", "sanction letter", "reco letter"]
};

// Takeover document (doc type id 118), shown alongside the recommendation
// letter. Same tolerant id-OR-name matching.
export const TAKEOVER_DOCUMENT_MATCH = {
    ids: [118],
    names: ["takeover", "take over", "take-over"]
};

// Portfolio View ("Existing Relationship" section): aggregated portfolio
// performance by dimension (Seller / Anchor / Geo / Industry / RBH). Values come
// already grouped in crores from GET case/{id}/portfolio-view/, so they render
// as plain numbers (the `(Cr.)` is in the header) — not via the money toggle.
export const PORTFOLIO_VIEW_COLUMNS = [
    { key: "category", label: "View" },
    { key: "vintage", label: "Vintage" },
    { key: "customers", label: "No of Customers", number: true },
    { key: "activePercent", label: "% Active", percent: true },
    // Exposure-size columns moved ahead of the PAR risk buckets so reviewers
    // see how big the exposure is before reading the delinquency %.
    { key: "aum", label: "AUM (Cr.)", number: true },
    { key: "throughput", label: "Throughput (Cr.)", number: true },
    { key: "par0", label: "PAR 0 (Cr.)", par: true, ratioKey: "aum", threshold: 5 },
    { key: "par30", label: "PAR 30 (Cr.)", par: true, ratioKey: "aum", threshold: 5 },
    { key: "par90", label: "PAR 90 (Cr.)", par: true, ratioKey: "aum", threshold: 1 },
    // Profitability column removed per credit-team request — the field is
    // still emitted by the API but not surfaced here.
    { key: "comments", label: "Comments", width: 240, wrap: true }
];

// KYC document type ids (mirrors the mapping used across the LOS document tabs).
export const KYC_DOCUMENT_TYPES = {
    PHOTO: [6],
    PAN: [7, 12],
    AADHAAR: [63, 8, 13]
};

// Credit bureau report document type ids.
export const BUREAU_DOCUMENT_TYPES = {
    REPORT: 51,
    SUPPORTING: 52
};

// Friendly labels for the KYC document tiles rendered per applicant.
export const KYC_DOCUMENT_TILES = [
    { key: "photo", label: "Photo", types: KYC_DOCUMENT_TYPES.PHOTO },
    { key: "pan", label: "PAN", types: KYC_DOCUMENT_TYPES.PAN },
    { key: "aadhaar", label: "Aadhaar", types: KYC_DOCUMENT_TYPES.AADHAAR }
];

// Company address-type ids (mirrors ADDRESSES_TYPES + final-credit-summary.js).
export const COMPANY_ADDRESS_TYPES = {
    REGISTERED: 1,
    OPERATING: 2
};

// Company document matchers. Document type ids are loaded dynamically by the
// backend, so each entry matches on BOTH candidate numeric ids AND case-
// insensitive name patterns (read off document.document_name). Matching on the
// name as well as the id means a stale/unknown id can't silently render a real
// document as "Not on file" — the failure mode we hit with KYC docs earlier.
// Doc type ids confirmed from this case's document list (see API_NOTES.md):
//   2  = GST Certificate (All pages)   3  = Shop Utility Bill
//   73 = Utility bills (electricity…)  78 = Shop Board
//   97 = GST Returns                   14 = Company Bank Statement (6 months)
//   74 = Current Account Statement     47 = Bank Documents
export const COMPANY_DOCUMENT_TYPES = {
    GST_CERTIFICATE: {
        ids: [2],
        names: ["gst certificate"]
    },
    UTILITY_BILL: {
        ids: [3, 73],
        names: ["shop utility bill", "utility bill"]
    },
    SHOP_PHOTO: {
        ids: [78],
        names: ["shop board"]
    },
    // Firm/company PAN (doc type id 1) — only relevant for Partnership cases.
    // findCompanyDoc restricts to company-level docs (no associate), so an
    // applicant's personal PAN can't match here.
    COMPANY_PAN: {
        ids: [1],
        names: ["Company PAN Card"]
    },
    // Partnership deed (doc type id 5) — only relevant for Partnership cases.
    PARTNERSHIP_DEED: {
        ids: [5],
        names: ["partnership deed"]
    },
    // Shareholding pattern / list of partners (doc type id 25) — only relevant
    // for Partnership cases.
    PARTNERS_LIST: {
        ids: [25],
        names: ["shareholding pattern", "list of partners"]
    }
};

// Source documents behind the GST / Ledger / Banking triangulation. Matched the
// same tolerant way (candidate ids OR document_name substrings) from the merged
// company + case document lists.
export const TRIANGULATION_DOCUMENT_TILES = [
    {
        // The uploaded "GST Returns" document (id 97) — fetched through the
        // internal document proxy, so it renders inline (unlike the external
        // live GST-pull URL, which the browser can only download).
        key: "gst",
        label: "GST Returns",
        icon: "file-protect",
        match: { ids: [97], names: ["gst returns"] }
    },
    {
        key: "ledger",
        label: "Ledger",
        icon: "file-text",
        match: { ids: [], names: ["ledger", "tally"] }
    },
    {
        key: "bank",
        label: "Bank Statement",
        icon: "bank",
        match: {
            ids: [14, 74, 47],
            names: [
                "company bank statement",
                "bank statement",
                "current account statement"
            ]
        }
    }
];

// CAM engine: maps the raw rule `category` to a display section title and icon
// (mirrors the CAM View). Several raw categories collapse into one section.
export const CAM_CATEGORY_TITLE_MAP = {
    Eligibility: "Eligibility",
    General: "Applicant Profile",
    GST: "GSTIN Analysis",
    Bureau: "Bureau Assessment",
    MPA: "Anchor Relationship",
    Anchor: "Anchor Relationship",
    Ledger: "Ledger",
    Banking: "Banking",
    Financials: "Financials",
    DB: "Portfolio Performance",
    Geography: "Portfolio Performance"
};

// Preferred display order of CAM category sections. Anything not listed falls
// to the end, in its natural order.
export const CAM_CATEGORY_ORDER = [
    // Eligibility gates every other category, so it leads.
    "Eligibility",
    "Applicant Profile",
    "GSTIN Analysis",
    "Banking",
    "Bureau Assessment",
    "Financials",
    "Anchor Relationship"
];

export const CAM_SECTION_ICON_MAP = {
    Eligibility: "safety-certificate",
    Financials: "bank",
    "Applicant Profile": "user",
    "Anchor Relationship": "apartment",
    "GSTIN Analysis": "file-text",
    Banking: "credit-card",
    "Credit History": "idcard",
    "Financial Performance": "line-chart",
    "Portfolio Performance": "pie-chart",
    "Bureau Assessment": "profile",
    Ledger: "reconciliation",
    Others: "appstore"
};

// Base company document tiles rendered in the Company Detail panel for every
// constitution. Constitution-specific tiles (Company PAN, Partnership Deed,
// Partners list) are appended by company-detail-section for the relevant
// constitutions — see COMPANY_PAN_TILE / PARTNERSHIP_DEED_TILE / PARTNERS_LIST_TILE.
export const COMPANY_DOCUMENT_TILES = [
    {
        key: "gstCertificate",
        label: "GST Certificate",
        icon: "file-protect",
        match: COMPANY_DOCUMENT_TYPES.GST_CERTIFICATE
    },
    {
        key: "utilityBill",
        label: "Utility Bill",
        icon: "file-text",
        match: COMPANY_DOCUMENT_TYPES.UTILITY_BILL
    },
    {
        key: "recommendationLetter",
        label: "Recommendation Letter",
        icon: "file-done",
        match: RECOMMENDATION_DOCUMENT_MATCH
    },
    {
        key: "takeoverDocument",
        label: "Takeover Document",
        icon: "swap",
        match: TAKEOVER_DOCUMENT_MATCH,
        // Filed against the case/an applicant rather than the company, so it is
        // resolved without findCompanyDoc's company-level-only restriction.
        anyDoc: true
    }
];

// Company PAN tile — appended to the verification documents only for
// Partnership-constitution cases (see company-detail-section). Kept separate so
// it isn't rendered for every constitution.
export const COMPANY_PAN_TILE = {
    key: "companyPan",
    label: "Company PAN",
    icon: "idcard",
    match: COMPANY_DOCUMENT_TYPES.COMPANY_PAN
};

// Partnership deed tile — also appended only for Partnership-constitution cases.
export const PARTNERSHIP_DEED_TILE = {
    key: "partnershipDeed",
    label: "Partnership Deed",
    icon: "file-text",
    match: COMPANY_DOCUMENT_TYPES.PARTNERSHIP_DEED
};

// Shareholding pattern / list of partners tile — Partnership-constitution only.
export const PARTNERS_LIST_TILE = {
    key: "partnersList",
    label: "Shareholding Pattern / List of Partners",
    icon: "team",
    match: COMPANY_DOCUMENT_TYPES.PARTNERS_LIST
};
